import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const chapters = [
  {
    name: 'Computer Society',
    description: 'Computación, IA y software avanzado para liderar la transformación digital.',
    logo: '/assets/capitulos/IEEE-CS_LogoTM-orange.png',
    accent: 'from-orange-500/10 to-amber-500/5',
    border: 'border-orange-500/30',
  },
  {
    name: 'Power & Energy Society',
    description: 'Soluciones eléctricas y energéticas sostenibles con alcance académico y social.',
    logo: '/assets/capitulos/ieee-pes-logo-1.webp',
    accent: 'from-green-500/10 to-emerald-500/5',
    border: 'border-green-500/30',
  },
  {
    name: 'Women in Engineering',
    description: 'Diversidad y liderazgo femenino en ingeniería y tecnología.',
    logo: '/assets/capitulos/WIE_purple_pms526.png',
    accent: 'from-fuchsia-500/10 to-pink-500/5',
    border: 'border-fuchsia-500/30',
  },
  {
    name: 'Robotics & Automation',
    description: 'Robótica, automatización y sistemas inteligentes que redefinen industrias.',
    logo: '/assets/capitulos/IEEE_RAS_logo_4C_stacked.png',
    accent: 'from-purple-500/10 to-red-500/5',
    border: 'border-purple-500/30',
  },
  {
    name: 'Communications Society',
    description: 'Redes y comunicaciones que conectan universidades, industrias y comunidades.',
    logo: '/assets/capitulos/ieee-comsoc.png',
    accent: 'from-blue-600/10 to-blue-500/5',
    border: 'border-blue-500/30',
  },
  {
    name: 'SIGHT',
    description: 'Tecnología con propósito social para resolver desafíos locales y globales.',
    logo: '/assets/capitulos/sight.png',
    accent: 'from-red-500/10 to-orange-500/5',
    border: 'border-red-500/30',
  },
  {
    name: 'APS',
    description: 'Automatización de procesos y aplicaciones industriales con impacto real.',
    logo: '/assets/capitulos/ieee-aps.png',
    accent: 'from-teal-500/10 to-cyan-500/5',
    border: 'border-teal-500/30',
  },
  {
    name: 'IES',
    description: 'Electrónica e instrumentación industrial para la ingeniería moderna.',
    logo: '/assets/capitulos/ieee-ies.png',
    accent: 'from-blue-500/10 to-orange-500/5',
    border: 'border-blue-500/30',
  },
  {
    name: 'EMBS',
    description: 'Ingeniería en medicina y biología para avances en salud y biotecnología.',
    logo: '/assets/capitulos/ieee-embs.png',
    accent: 'from-violet-600/10 to-purple-500/5',
    border: 'border-violet-500/30',
  },
];

const Chapters: React.FC = () => {
  const [selectedChapter, setSelectedChapter] = useState<{ name: string; logo: string } | null>(null);

  useEffect(() => {
    if (selectedChapter === null) {
      document.body.style.overflow = 'auto';
      return;
    }

    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedChapter(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedChapter]);

  const closeModal = () => setSelectedChapter(null);

  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-32 lg:py-40" id="chapters">
      {/* Ambient backgrounds */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] rounded-full blur-[150px] opacity-20 bg-gradient-to-l from-cyan-200" />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10 bg-gradient-to-br from-ieeeBlue" />
      </div>

      {/* Subtle Grid */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)',
          backgroundSize: '120px 120px'
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 sm:px-8 z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-8 mb-20"
        >
          <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 shadow-sm w-fit">
            <div className="w-1.5 h-1.5 rounded-full bg-ieeeBlue" />
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-600">Capítulos técnicos</span>
          </div>

          <div className="space-y-4 max-w-3xl">
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.15] tracking-tight text-slate-900">
              Comunidades de{'\u00A0'}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ieeeBlue to-cyan-500">
                especialidad
              </span>
            </h2>

            <p className="text-lg sm:text-xl leading-relaxed text-slate-600 font-light max-w-2xl">
              Cada capítulo representa una disciplina de ingeniería con su identidad, misión y comunidad de aprendizaje.
            </p>
          </div>
        </motion.div>

        {/* Chapters grid */}
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {chapters.map((chapter, index) => (
            <motion.button
              key={chapter.name}
              type="button"
              onClick={() => setSelectedChapter({ name: chapter.name, logo: chapter.logo })}
              whileHover={{ y: -4 }}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.06 }}
              viewport={{ once: true, margin: '-50px' }}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50 p-8 transition-all duration-300 hover:border-ieeeBlue/30 hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 cursor-pointer"
            >
              {/* Gradient accent on hover */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br ${chapter.accent} pointer-events-none`} />

              {/* Logo container */}
              <div className="relative mb-8 flex h-32 items-center justify-center rounded-xl bg-white border border-slate-100 shadow-sm">
                {chapter.logo ? (
                  <img
                    src={chapter.logo}
                    alt={`${chapter.name} logo`}
                    className="h-20 max-w-[90%] object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300 mix-blend-multiply"
                  />
                ) : (
                  <span className="text-xl font-bold text-slate-400 tracking-[0.2em] uppercase">
                    {chapter.name.split(' ').map(word => word[0]).join('')}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="relative space-y-3 text-left">
                <h3 className="text-xl font-semibold text-slate-900 leading-tight">{chapter.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-light">
                  {chapter.description}
                </p>

                {/* Arrow accent */}
                <div className="pt-3">
                  <svg className="w-5 h-5 text-ieeeBlue/40 group-hover:text-ieeeBlue transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedChapter !== null && (
          <motion.div
            onClick={closeModal}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(event) => event.stopPropagation()}
              className="relative w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
              initial={{ y: 24, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={closeModal}
                className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label="Cerrar"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>

              {/* Modal content */}
              <div className="p-12 text-center space-y-8 relative">
                <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-slate-50 to-transparent pointer-events-none" />

                <div className="relative z-10 flex h-48 items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm p-6 max-w-sm mx-auto">
                  <img
                    src={selectedChapter.logo || '/assets/logos_rama_General_llama_sola/llamasolablanco.png'}
                    alt={selectedChapter.name}
                    className="h-32 max-w-[90%] object-contain mix-blend-multiply"
                  />
                </div>

                <div className="space-y-4 relative z-10">
                  <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{selectedChapter.name}</h3>
                  <p className="text-slate-600 leading-relaxed font-light max-w-xl mx-auto">
                    Este capítulo representa una comunidad vibrante de estudiantes, profesores e investigadores unidos en torno a los principios de innovación, aprendizaje colaborativo y excelencia técnica en {selectedChapter.name.toLowerCase()}.
                  </p>
                </div>

                <div className="pt-6 relative z-10">
                  <button
                    onClick={closeModal}
                    className="inline-flex px-8 py-3 rounded-full bg-ieeeBlue text-white text-sm font-medium transition hover:bg-ieeeBlue/90 hover:scale-105 active:scale-95 shadow-md shadow-ieeeBlue/20"
                  >
                    Cerrar Vista
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Chapters;
